import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const BuchungskontenListe = () => {
  const [buchungskonten, setBuchungskonten] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedKonto, setSelectedKonto] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Form State
  const [formData, setFormData] = useState({
    buchungskonto: '',
    buchungskontokategorie: '',
    umlagefaehig: false,
    steuerrelevant: false,
    umsatzsteuer: '',
    art: 'AUSGABE',
    umlage_art: ''
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Delete Dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    konto: null,
    canDelete: false,
    usageCount: 0,
    buchungen: [],
    replacementKonten: [],
    deleteMode: 'bulk' // 'bulk', 'individual', 'force'
  });

  const [replacementKonto, setReplacementKonto] = useState('');
  const [individualMappings, setIndividualMappings] = useState({});

  const kategorien = [
    'Anschaffungskosten',
    'Betriebs-/Nebenkosten',
    'Verwaltungskosten',
    'Instandhaltungskosten',
    'Einnahmen',
    'Sonstige'
  ];

  const fetchBuchungskonten = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getBuchungskonten();
      setBuchungskonten(data);
    } catch (err) {
      console.error('Fehler beim Abrufen der Buchungskonten:', err);
      showSnackbar('Fehler beim Laden der Buchungskonten', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuchungskonten();
  }, [fetchBuchungskonten]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setFormData({
      buchungskonto: '',
      buchungskontokategorie: '',
      umlagefaehig: false,
      steuerrelevant: false,
      umsatzsteuer: '',
      art: 'AUSGABE',
      umlage_art: ''
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setSelectedKonto(null);
    setOpenDialog(true);
  };

  const openEditDialog = (konto) => {
    setFormData({
      buchungskonto: konto.buchungskonto,
      buchungskontokategorie: konto.buchungskontokategorie,
      umlagefaehig: konto.umlagefaehig || false,
      steuerrelevant: konto.steuerrelevant || false,
      umsatzsteuer: konto.umsatzsteuer || '',
      art: konto.art || 'AUSGABE',
      umlage_art: konto.umlage_art || ''
    });
    setSelectedKonto(konto);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await apiService.updateBuchungskonto(selectedKonto.buchungskontoid, formData);
        showSnackbar('Buchungskonto erfolgreich aktualisiert');
      } else {
        await apiService.createBuchungskonto(formData);
        showSnackbar('Buchungskonto erfolgreich erstellt');
      }
      setOpenDialog(false);
      fetchBuchungskonten();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      showSnackbar(err.message || 'Fehler beim Speichern', 'error');
    }
  };

  const checkDeletePossibility = async (konto) => {
    try {
      const response = await apiService.checkBuchungskontoCanDelete(konto.buchungskontoid);
      const otherKonten = buchungskonten.filter(k => k.buchungskontoid !== konto.buchungskontoid);
      
      // Initialisiere individuelle Mappings
      const initialMappings = {};
      if (response.buchungen) {
        response.buchungen.forEach(buchung => {
          initialMappings[buchung.buchungsid] = '';
        });
      }
      
      setDeleteDialog({
        open: true,
        konto,
        canDelete: response.canDelete,
        usageCount: response.usageCount,
        buchungen: response.buchungen || [],
        replacementKonten: otherKonten,
        deleteMode: 'bulk'
      });
      
      setIndividualMappings(initialMappings);
    } catch (err) {
      console.error('Fehler beim Prüfen der Löschbarkeit:', err);
      showSnackbar('Fehler beim Prüfen der Löschbarkeit', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      let payload = {};
      
      if (deleteDialog.deleteMode === 'individual') {
        // Individuelle Zuordnung
        payload.individualMappings = deleteDialog.buchungen.map(buchung => ({
          buchungsid: buchung.buchungsid,
          newBuchungskontoid: individualMappings[buchung.buchungsid] || 'DELETE'
        }));
      } else if (deleteDialog.deleteMode === 'bulk' && replacementKonto) {
        // Massenübertragung
        payload.replacementId = replacementKonto;
      } else if (deleteDialog.deleteMode === 'force') {
        // Force Delete
        payload.force = true;
      }
      await apiService.deleteBuchungskonto(deleteDialog.konto.buchungskontoid, payload);
      
      showSnackbar('Buchungskonto erfolgreich gelöscht');
      setDeleteDialog({ ...deleteDialog, open: false });
      setReplacementKonto('');
      setIndividualMappings({});
      fetchBuchungskonten();
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      showSnackbar(err.message || 'Fehler beim Löschen', 'error');
    }
  };

  const handleIndividualMappingChange = (buchungsid, newKontoId) => {
    setIndividualMappings(prev => ({
      ...prev,
      [buchungsid]: newKontoId
    }));
  };

  const setAllMappingsToSame = (kontoId) => {
    const newMappings = {};
    deleteDialog.buchungen.forEach(buchung => {
      newMappings[buchung.buchungsid] = kontoId;
    });
    setIndividualMappings(newMappings);
  };

  const getArtChip = (art) => {
    return (
      <Chip 
        label={art === 'EINNAHME' ? 'Einnahme' : 'Ausgabe'} 
        color={art === 'EINNAHME' ? 'success' : 'primary'}
        size="small"
      />
    );
  };

  const getUmlageArtChip = (umlageArt) => {
    if (!umlageArt) return '-';
    
    const colorMap = {
      'FLAECHENANTEIL': 'secondary',
      'VERBRAUCH': 'warning'
    };
    
    return (
      <Chip 
        label={umlageArt === 'FLAECHENANTEIL' ? 'Flächenanteil' : 'Verbrauch'} 
        color={colorMap[umlageArt]}
        size="small"
      />
    );
  };

  const filteredKonten = buchungskonten.filter(konto => {
    if (tabValue === 0) return true; // Alle
    if (tabValue === 1) return konto.art === 'EINNAHME';
    if (tabValue === 2) return konto.art === 'AUSGABE';
    if (tabValue === 3) return konto.umlagefaehig;
    return true;
  });

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ⚠️ ACHTUNG: Diese Ansicht ist veraltet!
          </Typography>
          <Typography variant="body2">
            Die <strong>buchungskonten-Tabelle</strong> wird nicht mehr verwendet und ist nur noch zu Migrations-Zwecken sichtbar.<br />
            Bitte verwenden Sie die neue <strong>Kontenplan-Verwaltung</strong> mit der <strong>konten-Tabelle</strong> (doppelte Buchführung).<br />
            <br />
            <strong>Neue Kontenstruktur:</strong><br />
            • Liquiditätskonten: 1000-1999 (Bankkonto, Privatkonto, Kasse)<br />
            • Sachkonten: 3000-6999 (Einnahmen, Betriebskosten, Verwaltung, Finanzierung)<br />
            • Standard-Konten sind vorgegeben und nicht editierbar<br />
            • Nur Custom-Konten können angelegt/bearbeitet werden
          </Typography>
        </Alert>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" gutterBottom>
            Buchungskonten Verwaltung (VERALTET)
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Neues Konto
          </Button>
        </Box>

        <Box mb={3}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label={`Alle (${buchungskonten.length})`} />
            <Tab label={`Einnahmen (${buchungskonten.filter(k => k.art === 'EINNAHME').length})`} />
            <Tab label={`Ausgaben (${buchungskonten.filter(k => k.art === 'AUSGABE').length})`} />
            <Tab label={`Umlagefähig (${buchungskonten.filter(k => k.umlagefaehig).length})`} />
          </Tabs>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Buchungskonto</TableCell>
                  <TableCell>Kategorie</TableCell>
                  <TableCell>Art</TableCell>
                  <TableCell>Umlagefähig</TableCell>
                  <TableCell>Umlage-Art</TableCell>
                  <TableCell>Steuerrelevant</TableCell>
                  <TableCell>USt</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredKonten.map((konto) => (
                  <TableRow key={konto.buchungskontoid}>
                    <TableCell>{konto.buchungskontoid}</TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ maxWidth: '200px' }}>
                        {konto.buchungskonto}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {konto.buchungskontokategorie}
                      </Typography>
                    </TableCell>
                    <TableCell>{getArtChip(konto.art)}</TableCell>
                    <TableCell>{konto.umlagefaehig ? 'Ja' : 'Nein'}</TableCell>
                    <TableCell>{getUmlageArtChip(konto.umlage_art)}</TableCell>
                    <TableCell>{konto.steuerrelevant ? 'Ja' : 'Nein'}</TableCell>
                    <TableCell>{konto.umsatzsteuer ? `${konto.umsatzsteuer}%` : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => openEditDialog(konto)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" onClick={() => checkDeletePossibility(konto)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editMode ? 'Buchungskonto bearbeiten' : 'Neues Buchungskonto'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Buchungskonto"
                value={formData.buchungskonto}
                onChange={(e) => setFormData({...formData, buchungskonto: e.target.value})}
                fullWidth
                required
              />
              
              <FormControl fullWidth required>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  value={formData.buchungskontokategorie}
                  onChange={(e) => setFormData({...formData, buchungskontokategorie: e.target.value})}
                >
                  {kategorien.map(kat => (
                    <MenuItem key={kat} value={kat}>{kat}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Art</InputLabel>
                <Select
                  value={formData.art}
                  onChange={(e) => setFormData({...formData, art: e.target.value})}
                >
                  <MenuItem value="EINNAHME">Einnahme</MenuItem>
                  <MenuItem value="AUSGABE">Ausgabe</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.umlagefaehig}
                    onChange={(e) => setFormData({
                      ...formData, 
                      umlagefaehig: e.target.checked,
                      umlage_art: e.target.checked ? formData.umlage_art : ''
                    })}
                  />
                }
                label="Umlagefähig"
              />

              {formData.umlagefaehig && (
                <FormControl fullWidth required>
                  <InputLabel>Umlage-Art</InputLabel>
                  <Select
                    value={formData.umlage_art}
                    onChange={(e) => setFormData({...formData, umlage_art: e.target.value})}
                  >
                    <MenuItem value="FLAECHENANTEIL">Flächenanteil</MenuItem>
                    <MenuItem value="VERBRAUCH">Verbrauch</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.steuerrelevant}
                    onChange={(e) => setFormData({...formData, steuerrelevant: e.target.checked})}
                  />
                }
                label="Steuerrelevant"
              />

              <TextField
                label="Umsatzsteuer (%)"
                type="number"
                value={formData.umsatzsteuer}
                onChange={(e) => setFormData({...formData, umsatzsteuer: e.target.value})}
                fullWidth
                inputProps={{ min: 0, max: 100 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editMode ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({...deleteDialog, open: false})} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon color="warning" />
              Buchungskonto löschen
            </Box>
          </DialogTitle>
          <DialogContent>
            {deleteDialog.canDelete ? (
              <Typography>
                Sind Sie sicher, dass Sie das Buchungskonto "{deleteDialog.konto?.buchungskonto}" löschen möchten?
              </Typography>
            ) : (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Dieses Buchungskonto wird in {deleteDialog.usageCount} Buchung(en) verwendet und kann nicht direkt gelöscht werden.
                </Alert>
                
                <Typography variant="h6" gutterBottom>
                  Löschoptionen:
                </Typography>
                
                <Box mb={3}>
                  <Tabs 
                    value={deleteDialog.deleteMode} 
                    onChange={(e, newMode) => setDeleteDialog({...deleteDialog, deleteMode: newMode})}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Tab label="Massenübertragung" value="bulk" />
                    <Tab label="Individuelle Zuordnung" value="individual" />
                    <Tab label="Alle löschen" value="force" />
                  </Tabs>
                </Box>

                {/* Massenübertragung */}
                {deleteDialog.deleteMode === 'bulk' && (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Alle {deleteDialog.usageCount} Buchungen auf ein anderes Konto übertragen:
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Ziel-Buchungskonto</InputLabel>
                      <Select
                        value={replacementKonto}
                        onChange={(e) => setReplacementKonto(e.target.value)}
                      >
                        {deleteDialog.replacementKonten?.map(konto => (
                          <MenuItem key={konto.buchungskontoid} value={konto.buchungskontoid}>
                            {konto.buchungskonto} ({konto.buchungskontokategorie})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Individuelle Zuordnung */}
                {deleteDialog.deleteMode === 'individual' && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2">
                        Jede Buchung einzeln zuordnen:
                      </Typography>
                      <Box>
                        <Button 
                          size="small" 
                          onClick={() => setAllMappingsToSame('')}
                          sx={{ mr: 1 }}
                        >
                          Alle löschen
                        </Button>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Alle setzen auf</InputLabel>
                          <Select
                            value=""
                            onChange={(e) => setAllMappingsToSame(e.target.value)}
                          >
                            {deleteDialog.replacementKonten?.map(konto => (
                              <MenuItem key={konto.buchungskontoid} value={konto.buchungskontoid}>
                                {konto.buchungskonto}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                    
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Datum</TableCell>
                            <TableCell>Betrag</TableCell>
                            <TableCell>Text</TableCell>
                            <TableCell>Datei</TableCell>
                            <TableCell>Neues Konto</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {deleteDialog.buchungen.map((buchung) => (
                            <TableRow key={buchung.buchungsid}>
                              <TableCell>
                                {new Date(buchung.buchungsdatum).toLocaleDateString('de-DE')}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color={buchung.buchungsbetrag >= 0 ? 'success.main' : 'error.main'}>
                                  {new Intl.NumberFormat('de-DE', { 
                                    style: 'currency', 
                                    currency: 'EUR' 
                                  }).format(buchung.buchungsbetrag)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {buchung.buchungstext || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {buchung.dateiname}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={individualMappings[buchung.buchungsid] || ''}
                                    onChange={(e) => handleIndividualMappingChange(buchung.buchungsid, e.target.value)}
                                  >
                                    <MenuItem value="">
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <DeleteIcon fontSize="small" color="error" />
                                        Löschen
                                      </Box>
                                    </MenuItem>
                                    {deleteDialog.replacementKonten?.map(konto => (
                                      <MenuItem key={konto.buchungskontoid} value={konto.buchungskontoid}>
                                        <Typography variant="body2">
                                          {konto.buchungskonto}
                                        </Typography>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {/* Force Delete */}
                {deleteDialog.deleteMode === 'force' && (
                  <Box>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      ⚠️ ACHTUNG: Alle {deleteDialog.usageCount} Buchungen werden unwiderruflich gelöscht!
                    </Alert>
                    <Typography variant="body2" color="error">
                      Diese Aktion kann nicht rückgängig gemacht werden. Alle mit diesem Konto verknüpften Buchungsdaten gehen verloren.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({...deleteDialog, open: false})}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleDelete} 
              color="error" 
              variant="contained"
              disabled={
                !deleteDialog.canDelete && 
                deleteDialog.deleteMode === 'bulk' && !replacementKonto
              }
            >
              {deleteDialog.deleteMode === 'individual' ? 'Zuordnen & Löschen' :
               deleteDialog.deleteMode === 'bulk' ? 'Übertragen & Löschen' : 
               'Löschen'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({...snackbar, open: false})}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default BuchungskontenListe;
